import { postRequest, getRequest } from "../Fetch/request";
import { retryAISync, enhanceAIError } from "./aiRetryUtils";
import { API_ENDPOINTS } from "./constants";

// Track active polling to prevent multiple simultaneous calls
let activePollingJobs = new Set();

// AI Provider Configuration - Using Claude only
const AI_PROVIDERS = {
    ANTHROPIC: {
        name: 'anthropic',
        defaultModel: 'claude-haiku-4-5-20251001'
    }
};

/**
 * Get AI feedback from Anthropic Claude via backend proxy (Synchronous - Default)
 * @param {string} systemPrompt - The system prompt for the AI
 * @param {string} userMessage - The user message/content
 * @param {string} userId - The user ID (optional)
 * @param {string} provider - The AI provider (defaults to 'anthropic')
 * @param {string} model - The specific model to use (optional, uses default Claude model)
 * @param {object} schema - JSON schema for structured output (optional)
 * @returns {Promise<string>} - The AI response content
 */
export const getAIFeedbackSync = async (systemPrompt, userMessage, userId = null, provider = 'anthropic', model = null, schema = null, options = {}) => {
    try {
        // Validate provider
        const providerConfig = AI_PROVIDERS[provider.toUpperCase()] || AI_PROVIDERS.ANTHROPIC;
        const modelToUse = model || providerConfig.defaultModel;

        // Prepare request payload
        const payload = {
            systemContent: systemPrompt,
            userContent: userMessage,
            model: modelToUse,
            provider: providerConfig.name,
            userId: userId,
            max_tokens: 150  // Limit response length for concise answers
        };

        // Add schema if provided
        if (schema) {
            payload.schema = schema;
        }

        // Use retry mechanism for the API call
        const response = await retryAISync(
            `${process.env.REACT_APP_URL}${API_ENDPOINTS.AI_FEEDBACK_SYNC}`,
            payload,
            {
                maxRetries: options.maxRetries || 3,
                schema: schema, // Pass schema for validation
                onRetry: (attempt, error, delay) => {
                    // Call user-provided retry callback if exists
                    if (options.onRetry) {
                        options.onRetry(attempt, error, delay);
                    }
                }
            }
        );

        return response.data.response;

    } catch (error) {
        const enhancedError = enhanceAIError(error, `${provider} AI sync call`);
        console.error(`❌ AI Sync Call - Final failure:`, enhancedError.message);
        throw enhancedError;
    }
};

/**
 * Get AI feedback from Anthropic Claude via backend proxy (Async - Legacy)
 * @param {string} systemPrompt - The system prompt for the AI
 * @param {string} userMessage - The user message/content
 * @param {string} userId - The user ID (optional)
 * @param {string} provider - The AI provider (defaults to 'anthropic')
 * @param {string} model - The specific model to use (optional, uses default Claude model)
 * @returns {Promise<string>} - The AI response content
 */
export const getAIFeedback = async (systemPrompt, userMessage, userId = null, provider = 'anthropic', model = null) => {
    try {
        // Validate provider
        const providerConfig = AI_PROVIDERS[provider.toUpperCase()] || AI_PROVIDERS.ANTHROPIC;
        const modelToUse = model || providerConfig.defaultModel;

        // Step 1: Create async job
        const jobResponse = await postRequest(`${process.env.REACT_APP_URL}/ai/async`, {
            systemContent: systemPrompt,
            userContent: userMessage,
            model: modelToUse,
            provider: providerConfig.name,
            userId: userId
        });

        if (!jobResponse || !jobResponse.success || !jobResponse.data || !jobResponse.data.jobId) {
            throw new Error('Failed to create job');
        }

        const jobId = jobResponse.data.jobId;

        // Check if this job is already being polled
        if (activePollingJobs.has(jobId)) {
            throw new Error('Job already being processed');
        }

        // Add job to active polling set
        activePollingJobs.add(jobId);

        try {
            // Step 2: Poll for completion
            const result = await pollForJobCompletion(jobId);
            return result;
        } finally {
            // Remove job from active polling set
            activePollingJobs.delete(jobId);
        }

    } catch (error) {
        throw new Error(`${provider} API error: ${error.message}`);
    }
};

/**
 * Get AI feedback with mode selection (Sync or Async)
 * @param {string} systemPrompt - The system prompt for the AI
 * @param {string} userMessage - The user message/content
 * @param {string} mode - 'sync' or 'async' (defaults to 'async')
 * @param {string} userId - The user ID (optional)
 * @param {string} provider - The AI provider (defaults to 'anthropic')
 * @param {string} model - The specific model to use (optional, uses default Claude model)
 * @returns {Promise<string>} - The AI response content
 */
export const getAIFeedbackWithMode = async (systemPrompt, userMessage, mode = 'async', userId = null, provider = 'anthropic', model = null) => {
    if (mode === 'sync') {
        return getAIFeedbackSync(systemPrompt, userMessage, userId, provider, model);
    } else {
        return getAIFeedback(systemPrompt, userMessage, userId, provider, model);
    }
};

/**
 * Poll for job completion with exponential backoff.
 * Starts at 500ms, doubles each attempt, caps at 8s.
 * Much faster than the old flat 5s interval (saves 30-60s on typical AI calls).
 */
const pollForJobCompletion = async (jobId, maxAttempts = 20) => {
    let attempts = 0;
    let interval = 500; // start at 500ms
    const MAX_INTERVAL = 8000; // cap at 8s

    while (attempts < maxAttempts) {
        try {
            const statusResponse = await getRequest(`${process.env.REACT_APP_URL}/ai/status/${jobId}`);

            if (!statusResponse || !statusResponse.success) {
                throw new Error('Invalid status response');
            }

            const { status, result, message } = statusResponse.data;

            if (status === 'completed' && result && result.response !== undefined) {
                return result.response;
            } else if (status === 'failed') {
                throw new Error(`Job failed: ${message || 'Unknown error'}`);
            } else if (status === 'pending' || status === 'processing') {
                await new Promise(resolve => setTimeout(resolve, interval));
                interval = Math.min(interval * 2, MAX_INTERVAL);
            } else {
                throw new Error(`Unknown job status: ${status}`);
            }

        } catch (error) {
            if (error.message.includes('Job failed:')) {
                throw error;
            }
            // On transient errors, still back off before retrying
            await new Promise(resolve => setTimeout(resolve, interval));
            interval = Math.min(interval * 2, MAX_INTERVAL);
        }

        attempts++;
    }

    throw new Error(`Job timeout after ${maxAttempts} attempts`);
};

/**
 * Poll for job completion with status updates and exponential backoff.
 */
const pollForJobCompletionWithStatus = async (jobId, onStatusUpdate = null, maxAttempts = 20) => {
    let attempts = 0;
    let interval = 500;
    const MAX_INTERVAL = 8000;

    while (attempts < maxAttempts) {
        try {
            const statusResponse = await getRequest(`${process.env.REACT_APP_URL}/ai/status/${jobId}`);

            if (!statusResponse || !statusResponse.success) {
                throw new Error('Invalid status response');
            }

            const { status, result, message } = statusResponse.data;

            if (status === 'completed' && result && result.response !== undefined) {
                if (onStatusUpdate) onStatusUpdate('Completed!');
                return result.response;
            } else if (status === 'failed') {
                if (onStatusUpdate) onStatusUpdate('Failed');
                throw new Error(`Job failed: ${message || 'Unknown error'}`);
            } else if (status === 'pending' || status === 'processing') {
                const statusMsg = status === 'pending' ? `Pending... (${attempts + 1})` : `Processing... (${attempts + 1})`;
                if (onStatusUpdate) onStatusUpdate(statusMsg);
                await new Promise(resolve => setTimeout(resolve, interval));
                interval = Math.min(interval * 2, MAX_INTERVAL);
            } else {
                throw new Error(`Unknown job status: ${status}`);
            }

            attempts++;

        } catch (error) {
            if (error.message.includes('Job failed:')) {
                throw error;
            }
            await new Promise(resolve => setTimeout(resolve, interval));
            interval = Math.min(interval * 2, MAX_INTERVAL);
            attempts++;
        }
    }

    if (onStatusUpdate) onStatusUpdate('Timeout');
    throw new Error(`Job timeout after ${maxAttempts} attempts`);
};

/**
 * Enhanced version with loading state callback
 * @param {string} systemPrompt - The system prompt for the AI
 * @param {string} userMessage - The user message/content
 * @param {string} userId - The user ID (optional)
 * @param {function} onStatusUpdate - Callback for status updates
 * @param {string} provider - The AI provider (defaults to 'anthropic')
 * @param {string} model - The specific model to use (optional, uses default Claude model)
 * @returns {Promise<string>} - The AI response content
 */
export const getAIFeedbackWithStatus = async (systemPrompt, userMessage, userId = null, onStatusUpdate = null, provider = 'anthropic', model = null) => {
    try {
        // Validate provider
        const providerConfig = AI_PROVIDERS[provider.toUpperCase()] || AI_PROVIDERS.ANTHROPIC;
        const modelToUse = model || providerConfig.defaultModel;

        // Step 1: Create async job
        if (onStatusUpdate) onStatusUpdate('Creating job...');
        
        const jobResponse = await postRequest(`${process.env.REACT_APP_URL}/ai/async`, {
            systemContent: systemPrompt,
            userContent: userMessage,
            model: modelToUse,
            provider: providerConfig.name,
            userId: userId
        });

        if (!jobResponse || !jobResponse.success || !jobResponse.data || !jobResponse.data.jobId) {
            throw new Error('Failed to create job');
        }

        const jobId = jobResponse.data.jobId;
        if (onStatusUpdate) onStatusUpdate('Job created, processing...');

        // Step 2: Poll for completion with status updates
        const result = await pollForJobCompletionWithStatus(jobId, onStatusUpdate);
        return result;

    } catch (error) {
        throw new Error(`${provider} API error: ${error.message}`);
    }
};

// Convenience functions for Claude (Sync - Default)
export const getClaudeFeedback = (systemPrompt, userMessage, userId = null, options = {}) => 
    getAIFeedbackSync(systemPrompt, userMessage, userId, 'anthropic', null, null, options);

export const getClaudeFeedbackSync = (systemPrompt, userMessage, userId = null, options = {}) => 
    getAIFeedbackSync(systemPrompt, userMessage, userId, 'anthropic', null, null, options);

// Legacy async functions (for backward compatibility)
export const getClaudeFeedbackAsync = (systemPrompt, userMessage, userId = null) => 
    getAIFeedback(systemPrompt, userMessage, userId, 'anthropic');

export const getClaudeFeedbackWithStatus = (systemPrompt, userMessage, userId = null, onStatusUpdate = null) => 
    getAIFeedbackWithStatus(systemPrompt, userMessage, userId, onStatusUpdate, 'anthropic');

// Convenience functions with mode selection
export const getClaudeFeedbackWithMode = (systemPrompt, userMessage, mode = 'sync', userId = null) => 
    getAIFeedbackWithMode(systemPrompt, userMessage, mode, userId, 'anthropic');
